import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { MoreHorizontal, FilePlus, ExternalLink, Ban, BellPlus, User, Briefcase, Pencil } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const custUrl = (r) => (r.customer_id ? `/admin/customers?open=${r.customer_id}` : null);
export const jobUrl = (r) => (r.appointment_id ? `/admin/schedule?open=${r.appointment_id}` : null);
export const invUrl = (r, edit) => (r.record_type === 'invoice' ? `/admin/invoices?open=${r.id}${edit ? '&edit=1' : ''}` : null);
const QB = [['needs_review', 'Needs Review'], ['ready_for_qb', 'Ready for QB'], ['entered_in_qb', 'Entered in QB'], ['paid', 'Paid'], ['on_hold', 'On Hold']];

const NoInvoiceDialog = ({ row, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/unfinished-billing/${row.appointment_id}/override`, { reason }, { withCredentials: true });
      toast.success('Marked No Invoice Needed — recorded in the job history');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md" data-testid="no-invoice-dialog">
        <DialogHeader><DialogTitle>No Invoice Needed / Not Billable</DialogTitle></DialogHeader>
        <p className="text-xs text-[#A3A3A3]">{row.customer_name} · {row.service_date}. The completion record stays as it is; your name, the time and this reason are saved to the job history.</p>
        <Textarea data-testid="no-invoice-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is no invoice needed? (min 5 characters)" className="bg-[#0A0A0A] border-[#262626] text-white" />
        <Button data-testid="no-invoice-save" disabled={busy || reason.trim().length < 5} onClick={save} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">Confirm</Button>
      </DialogContent>
    </Dialog>
  );
};

const Item = ({ to, onClick, icon: Icon, children, testid }) => (
  to ? <DropdownMenuItem asChild className="text-white text-xs cursor-pointer"><Link to={to} data-testid={testid}><Icon className="w-3.5 h-3.5 mr-2" />{children}</Link></DropdownMenuItem>
    : <DropdownMenuItem onClick={onClick} data-testid={testid} className="text-white text-xs cursor-pointer"><Icon className="w-3.5 h-3.5 mr-2" />{children}</DropdownMenuItem>
);

// Contextual resolve actions per Follow-Up row — every action opens/uses the canonical record.
export const FollowUpRowActions = ({ r, onFollowUp, onCreateInvoice, onChanged }) => {
  const [noInv, setNoInv] = useState(false);
  const isJob = r.record_type === 'job';
  const isData = r.categories.includes('data_issue');
  const setQb = async (s) => {
    try { await axios.post(`${API}/invoices/${r.id}/office-status`, { office_billing_status: s }, { withCredentials: true }); toast.success('QB status updated'); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Not permitted'); }
  };
  return (
    <div className="flex items-center gap-1">
      {isJob && <button data-testid={`followup-create-invoice-${r.id}`} onClick={() => onCreateInvoice(r)} className="text-[11px] font-bold px-2 py-1 rounded bg-[#D4AF37] text-black hover:bg-[#F3E5AB] inline-flex items-center gap-1 whitespace-nowrap"><FilePlus className="w-3 h-3" /> Create Invoice</button>}
      {!isJob && <Link data-testid={`followup-open-invoice-${r.id}`} to={invUrl(r)} className="text-[11px] font-bold px-2 py-1 rounded border border-[#3f3f3f] text-white hover:border-[#D4AF37] inline-flex items-center gap-1 whitespace-nowrap"><ExternalLink className="w-3 h-3" /> Open Invoice</Link>}
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`followup-actions-${r.id}`} className="p-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white"><MoreHorizontal className="w-4 h-4" /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] w-56">
          {jobUrl(r) && <Item to={jobUrl(r)} icon={Briefcase} testid={`followup-action-open-job-${r.id}`}>Open Job / Visit</Item>}
          {custUrl(r) && <Item to={custUrl(r)} icon={User} testid={`followup-action-open-customer-${r.id}`}>Open Customer{isData ? ' (fix linkage)' : ''}</Item>}
          {!isJob && <Item to={invUrl(r, true)} icon={Pencil} testid={`followup-action-edit-invoice-${r.id}`}>Edit / View Invoice{isData ? ' (fix linkage)' : ''}</Item>}
          <Item onClick={() => onFollowUp(r)} icon={BellPlus} testid={`followup-action-followup-${r.id}`}>{r.followup && !r.followup.resolved ? 'Update Follow-Up' : 'Follow Up'}</Item>
          {isJob && <><DropdownMenuSeparator className="bg-[#262626]" /><Item onClick={() => setNoInv(true)} icon={Ban} testid={`followup-action-no-invoice-${r.id}`}>Mark No Invoice Needed</Item></>}
          {!isJob && <><DropdownMenuSeparator className="bg-[#262626]" />
            <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#737373]">QB status</p>
            {QB.map(([k, l]) => <DropdownMenuItem key={k} data-testid={`followup-qb-${k}-${r.id}`} onClick={() => setQb(k)} className={`text-xs cursor-pointer ${r.qb_status === k ? 'text-[#D4AF37]' : 'text-white'}`}>{l}</DropdownMenuItem>)}
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
      {noInv && <NoInvoiceDialog row={r} onClose={() => setNoInv(false)} onDone={() => { setNoInv(false); onChanged(); }} />}
    </div>
  );
};

export default FollowUpRowActions;
